import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { Box, Button } from '@mui/material'

import { BookingAddressType } from 'generated/graphql'
import { ConsignmentPair } from 'hooks/booking/useBookingAddresses'
import { AddressEta } from 'hooks/useAllAddressesEta'

import {
  DeliveryPosition,
  JourneyDetails,
  LegalDocuments,
  DeliveryTimeEstimate,
  Details,
} from './ui'

interface TrackingInfoSectionProps {
  onDetailsClick?: () => void
  isMobile?: boolean
  booking?: any
  bookingCurrentAction?: any
  nextBookingAction?: any
  consignmentPairs?: ConsignmentPair[]
  nextAddressEta?: string | null
  etaWindowMinutes?: number | null
  stopName?: string | null
  stopAddress?: string | null
  etaLoading?: boolean
  onProofOfDeliveryClick?: () => void
  onProofOfCollectionClick?: () => void
  referenceAddressType?: BookingAddressType | null
  collectionAttachmentCount?: number
  deliveryAttachmentCount?: number
  addressEtas?: AddressEta[]
  talkToAnExpertUrl: string
}

const flexContainerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  px: 2,
  py: 2,
  height: '100%',
}

const contentWrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
}

const signUpButtonStyles = {
  background: 'linear-gradient(135deg, #471D7C 0%, #C6007E 100%)',
  color: 'white',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none' as const,
  borderRadius: '8px',
  py: 1.5,
  mt: 2,
  border: 0,
  '&:hover': {
    background: 'linear-gradient(135deg, #3a1766 0%, #a5006a 100%)',
  },
}

const getETAAddressType = (
  referenceAddressType: BookingAddressType | undefined | null,
  bookingCurrentAction: any,
): 'collection' | 'delivery' => {
  if (referenceAddressType === BookingAddressType.Collection) return 'collection'
  if (referenceAddressType === BookingAddressType.Delivery) return 'delivery'
  if ((bookingCurrentAction as any)?.signedAt) return 'delivery'
  if (bookingCurrentAction?.type === BookingAddressType.Collection) return 'collection'
  return 'delivery'
}

export const TrackingInfoSection = ({
  onDetailsClick,
  isMobile = false,
  booking,
  bookingCurrentAction,
  nextBookingAction,
  consignmentPairs,
  nextAddressEta,
  etaWindowMinutes,
  stopName,
  stopAddress,
  etaLoading = false,
  onProofOfDeliveryClick,
  onProofOfCollectionClick,
  referenceAddressType,
  collectionAttachmentCount = 0,
  deliveryAttachmentCount = 0,
  addressEtas = [],
  talkToAnExpertUrl,
}: TrackingInfoSectionProps) => {
  const etaAddressType: 'collection' | 'delivery' = getETAAddressType(
    referenceAddressType,
    bookingCurrentAction,
  )

  const handleContactClick = () => {
    window.open(talkToAnExpertUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box sx={isMobile ? { ...flexContainerStyles, py: 1 } : flexContainerStyles}>
      <Box sx={contentWrapperStyles}>
        <Box sx={{ mb: 1 }}>
          <LegalDocuments
            ourReference={booking?.ourReference}
            bookingCurrentAction={bookingCurrentAction}
            nextBookingAction={nextBookingAction}
          />
          {!isMobile && (
            <DeliveryTimeEstimate
              variant='inline'
              nextAddressEta={nextAddressEta}
              etaWindowMinutes={etaWindowMinutes}
              stopName={stopName}
              stopAddress={stopAddress}
              loading={etaLoading}
              addressType={etaAddressType}
              bookingStatus={booking?.status}
            />
          )}

          <Details
            onDetailsClick={onDetailsClick}
            booking={booking}
            bookingCurrentAction={bookingCurrentAction}
            nextBookingAction={nextBookingAction}
            consignmentPairs={consignmentPairs}
            referenceAddressType={referenceAddressType}
            addressEtas={addressEtas}
          />

          <DeliveryPosition
            deliveryPosition={booking?.deliveryPosition}
            totalDeliveries={booking?.totalDeliveries}
            currentDriverDelivery={booking?.currentDriverDelivery}
            isMobile={isMobile}
          />
        </Box>
        <JourneyDetails status={booking?.statusLabel} />
        {collectionAttachmentCount > 0 || deliveryAttachmentCount > 0 ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {collectionAttachmentCount > 0 && (
              <Button
                fullWidth
                variant='outlined'
                endIcon={<DescriptionOutlinedIcon />}
                onClick={onProofOfCollectionClick}
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #E0E0E0',
                  color: '#414651',
                  textTransform: 'none',
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '13.33px',
                  lineHeight: '150%',
                  letterSpacing: '0.15px',
                  backgroundColor: '#fff',
                  '&:hover': { backgroundColor: '#F5F5F5', border: '1px solid #E0E0E0' },
                }}>
                Proof of Collection
              </Button>
            )}
            {deliveryAttachmentCount > 0 && (
              <Button
                fullWidth
                variant='outlined'
                endIcon={<DescriptionOutlinedIcon />}
                onClick={onProofOfDeliveryClick}
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #E0E0E0',
                  color: '#414651',
                  textTransform: 'none',
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '13.33px',
                  lineHeight: '150%',
                  letterSpacing: '0.15px',
                  backgroundColor: '#fff',
                  '&:hover': { backgroundColor: '#F5F5F5', border: '1px solid #E0E0E0' },
                }}>
                Proof of Delivery
              </Button>
            )}
          </Box>
        ) : null}
        <Button fullWidth variant='contained' sx={signUpButtonStyles} onClick={handleContactClick}>
          Contact us for your deliveries
        </Button>
      </Box>
    </Box>
  )
}
